import { effect, Injectable, signal, inject } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { SiThemeService, ThemeType } from '@siemens/element-ng/theme';

import { Workspace } from '../DataTypes';

const STORAGE_KEY_THEME = 'theme';
const STORAGE_KEY_LANG = 'lang';
const STORAGE_KEY_DEFAULT_WORKSPACE = 'defaultWorkspaceId';
const DEFAULT_THEME: ThemeType = 'dark';

@Injectable({ providedIn: 'root' })
export class SettingsService {
  private translate = inject(TranslateService);
  private themeService = inject(SiThemeService);

  readonly availableLanguages = ['en', 'de'];

  /** Writable signal for internal language state */
  private readonly _language = signal<string>('');

  /** Readonly signal exposed to consumers for reactive language access */
  readonly language = this._language.asReadonly();

  /** Writable signal for internal theme state */
  private readonly _theme = signal<ThemeType>(this.loadTheme());

  /** Readonly signal exposed to consumers for reactive theme access */
  readonly theme = this._theme.asReadonly();

  /** Workspace the user picked as default, `undefined` means "fall back to Public" */
  private readonly _defaultWorkspaceId = signal<number | undefined>(this.loadDefaultWorkspaceId());

  /** Readonly signal exposed to consumers for reactive default workspace access */
  readonly defaultWorkspaceId = this._defaultWorkspaceId.asReadonly();

  constructor() {
    const translate = this.translate;

    translate.setFallbackLang('en');
    translate.onLangChange.subscribe(this.storeLanguage);

    const language = this.loadLanguage() ?? translate.getBrowserLang() ?? 'en';
    this.setLanguage(language);

    // Apply initial theme
    this.themeService.applyThemeType(this._theme());

    // Effect to persist theme changes and apply to theme service
    effect(() => {
      const currentTheme = this._theme();
      this.themeService.applyThemeType(currentTheme);
      localStorage.setItem(STORAGE_KEY_THEME, currentTheme);
    });
  }

  setLanguage(language: string) {
    this.translate.use(language);
    this._language.set(language);
  }

  setTheme(theme: ThemeType) {
    this._theme.set(theme);
  }

  setDefaultWorkspaceId(workspaceId: number | undefined) {
    this._defaultWorkspaceId.set(workspaceId);

    if (workspaceId === undefined) {
      localStorage.removeItem(STORAGE_KEY_DEFAULT_WORKSPACE);
    } else {
      localStorage.setItem(STORAGE_KEY_DEFAULT_WORKSPACE, String(workspaceId));
    }
  }

  /** Falls back to the Public workspace while nothing is stored or the stored one is gone. */
  resolveDefaultWorkspace(workspaces: Workspace[]): Workspace | undefined {
    const publicWorkspace = workspaces.find(
      workspace => workspace.name === 'Public' && !workspace.isPrivate
    );
    const storedId = this._defaultWorkspaceId();
    if (storedId === undefined) return publicWorkspace;

    return workspaces.find(workspace => workspace.id === storedId) ?? publicWorkspace;
  }

  private storeLanguage(event: LangChangeEvent) {
    localStorage.setItem(STORAGE_KEY_LANG, event.lang);
  }

  private loadLanguage() {
    return localStorage.getItem(STORAGE_KEY_LANG);
  }

  private loadTheme(): ThemeType {
    const theme = localStorage.getItem(STORAGE_KEY_THEME) as ThemeType | null;
    return theme ?? DEFAULT_THEME;
  }

  private loadDefaultWorkspaceId(): number | undefined {
    const stored = localStorage.getItem(STORAGE_KEY_DEFAULT_WORKSPACE);
    if (stored === null) return undefined;

    const workspaceId = Number(stored);
    return Number.isFinite(workspaceId) ? workspaceId : undefined;
  }
}
